import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Shuffle, RotateCcw, Save, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TarotCard, Card } from "@/components/TarotCard";

type Slot = { position: number; label: string; card_id: string | null; reversed: boolean; face_up: boolean; note: string; x: number; y: number; cross?: boolean };

const shuffle = <T,>(a: T[]) => { const c = [...a]; for (let i = c.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [c[i], c[j]] = [c[j], c[i]]; } return c; };

export default function TablePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const bookingId = sp.get("booking");
  const [deck, setDeck] = useState<Card[]>([]);
  const [spreads, setSpreads] = useState<any[]>([]);
  const [spread, setSpread] = useState<any>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [pile, setPile] = useState<Card[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [custom, setCustom] = useState(3);
  const [saved, setSaved] = useState<any[]>([]);
  const byId = useMemo(() => Object.fromEntries(deck.map((c) => [c.id, c])), [deck]);

  useEffect(() => {
    api.get("/table/deck").then((r) => setDeck(r.data.cards));
    api.get("/table/spreads").then((r) => { setSpreads(r.data); pick(r.data[1]); });
    if (user) api.get("/table/snapshots").then((r) => setSaved(r.data));
  }, [user]);
  useEffect(() => { if (deck.length) setPile(shuffle(deck)); }, [deck]);

  const pick = (s: any, n?: number) => {
    const positions = s.id === "custom" ? Array.from({ length: n || custom }, (_, i) => ({ label: `Position ${i + 1}`, x: 12 + (76 * i) / Math.max(1, (n || custom) - 1), y: 50 })) : s.positions;
    setSpread(s);
    setSlots(positions.map((p: any, i: number) => ({ position: i, label: p.label, card_id: null, reversed: false, face_up: false, note: "", x: p.x, y: p.y, cross: p.cross })));
    setActive(null);
    setPile(shuffle(deck));
  };
  const draw = (i: number) => {
    if (slots[i].card_id) { setSlots((s) => s.map((x, k) => k === i ? { ...x, face_up: !x.face_up } : x)); setActive(i); return; }
    const [card, ...rest] = pile;
    if (!card) return;
    setPile(rest);
    setSlots((s) => s.map((x, k) => k === i ? { ...x, card_id: card.id, face_up: true, reversed: Math.random() < 0.25 } : x));
    setActive(i);
  };
  const drop = (i: number, e: React.DragEvent) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("card");
    if (!id) return;
    setPile((p) => p.filter((c) => c.id !== id));
    setSlots((s) => s.map((x, k) => k === i ? { ...x, card_id: id, face_up: true, reversed: false } : x));
    setActive(i);
  };
  const update = (i: number, patch: Partial<Slot>) => setSlots((s) => s.map((x, k) => k === i ? { ...x, ...patch } : x));
  const reset = () => pick(spread);
  const save = async () => {
    if (!user) return nav("/login?next=/table");
    try {
      const { data } = await api.post("/table/snapshots", { title: title || `${spread.name} · ${new Date().toLocaleDateString()}`, spread_id: spread.id, spread_name: spread.name,
        slots: slots.map(({ position, label, card_id, reversed, face_up, note }) => ({ position, label, card_id, reversed, face_up, note })), notes, booking_id: bookingId || null });
      setSaved((s) => [data, ...s]);
      toast.success(bookingId ? "Snapshot saved to this reading" : "Snapshot saved");
      if (bookingId) nav(`/readings/${bookingId}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const loadSnap = (s: any) => {
    const sp = spreads.find((x) => x.id === s.spread_id) || spreads[1];
    setSpread(sp); setTitle(s.title); setNotes(s.notes);
    setSlots(s.slots.map((sl: any, i: number) => ({ ...sl, x: sp.positions[i]?.x ?? 50, y: sp.positions[i]?.y ?? 50, cross: sp.positions[i]?.cross })));
  };
  const del = async (id: string) => { await api.delete(`/table/snapshots/${id}`); setSaved((s) => s.filter((x) => x.id !== id)); };

  const a = active !== null ? slots[active] : null;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 text-[#F6F4EE]" data-testid="table-workspace">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="eyebrow !text-[#C5A059]">The Table{bookingId ? " · attached to a reading" : ""}</p>
          <h1 className="text-4xl font-semibold leading-none">A quiet place to pull cards.</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {spreads.map((s) => <button key={s.id} onClick={() => pick(s)} className={`chip !bg-transparent !border-[#2A2825] !text-[#F6F4EE] ${spread?.id === s.id ? "!bg-[#F6F4EE] !text-[#121110]" : ""}`} data-testid={`spread-${s.id}`}>{s.name}</button>)}
          <div className="flex items-center gap-1">
            <button onClick={() => pick({ id: "custom", name: `Custom ${custom}-card` })} className={`chip !bg-transparent !border-[#2A2825] !text-[#F6F4EE] ${spread?.id === "custom" ? "!bg-[#F6F4EE] !text-[#121110]" : ""}`} data-testid="spread-custom">Custom</button>
            <input type="number" min={1} max={12} value={custom} onChange={(e) => { const n = +e.target.value; setCustom(n); if (spread?.id === "custom") pick({ id: "custom", name: `Custom ${n}-card` }, n); }} className="w-12 bg-transparent border border-[#2A2825] rounded-full px-2 py-1 text-xs mono text-center" data-testid="custom-count" />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <div>
          <div className="table-surface rounded-lg border border-[#2A2825] relative h-[420px] sm:h-[520px] overflow-hidden" data-testid="table-canvas">
            {slots.map((s, i) => (
              <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5" style={{ left: `${s.x}%`, top: `${s.y}%`, transform: `translate(-50%,-50%) ${s.cross ? "rotate(90deg)" : ""}`, zIndex: s.cross ? 2 : 1 }}
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => drop(i, e)} data-testid={`table-card-slot-${i}`}>
                {s.card_id ? <TarotCard card={byId[s.card_id]} reversed={s.reversed} faceUp={s.face_up} onClick={() => draw(i)} className={active === i ? "ring-2 ring-[#C5A059]" : ""} /> :
                  <div onClick={() => draw(i)} className={`w-20 h-32 rounded-md border border-dashed cursor-pointer flex items-center justify-center transition-colors ${active === i ? "border-[#C5A059]" : "border-[#C5A059]/40 hover:border-[#C5A059]"}`}><span className="mono text-[9px] text-[#C5A059]/70">TAP</span></div>}
                {!s.cross && <span className="mono text-[9px] uppercase tracking-wider text-[#A8A59C] text-center whitespace-nowrap">{s.label}</span>}
              </div>
            ))}
            <div className="absolute left-3 bottom-3 flex items-center gap-3">
              <TarotCard faceUp={false} size="sm" draggable={pile.length > 0} onDragStart={(e) => e.dataTransfer.setData("card", pile[0]?.id)} onClick={() => { const i = slots.findIndex((s) => !s.card_id); if (i >= 0) draw(i); }} />
              <div className="mono text-[10px] text-[#A8A59C]"><p>{pile.length} in deck</p><p className="text-[#C5A059]/70">tap or drag</p></div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={() => setPile(shuffle(pile))} className="btn-ghost !border-[#2A2825] !text-[#F6F4EE] hover:!bg-[#F6F4EE] hover:!text-[#121110] !py-2 text-xs" data-testid="shuffle-btn"><Shuffle size={14} /> Shuffle</button>
            <button onClick={reset} className="btn-ghost !border-[#2A2825] !text-[#F6F4EE] hover:!bg-[#F6F4EE] hover:!text-[#121110] !py-2 text-xs" data-testid="reset-btn"><RotateCcw size={14} /> Clear</button>
            <button onClick={() => setSlots((s) => s.map((x) => ({ ...x, face_up: true })))} className="btn-ghost !border-[#2A2825] !text-[#F6F4EE] hover:!bg-[#F6F4EE] hover:!text-[#121110] !py-2 text-xs" data-testid="reveal-btn"><Eye size={14} /> Reveal all</button>
            <button onClick={save} className="btn-brass !py-2 text-xs ml-auto" data-testid="save-snapshot-btn"><Save size={14} /> Save snapshot</button>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-md border border-[#2A2825] bg-[#1A1917] p-4" data-testid="slot-inspector">
            {a ? (
              <>
                <p className="eyebrow !text-[#C5A059]">{a.label}</p>
                <p className="serif text-2xl mt-1">{a.card_id ? byId[a.card_id]?.name : "Empty"}{a.reversed ? <span className="mono text-xs text-[#A8A59C]"> · reversed</span> : ""}</p>
                {a.card_id && <p className="text-sm text-[#A8A59C] mt-1">{byId[a.card_id]?.keywords}</p>}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => update(active!, { reversed: !a.reversed })} disabled={!a.card_id} className="chip !bg-transparent !border-[#2A2825] !text-[#F6F4EE]" data-testid="toggle-reversal-btn">Toggle reversal</button>
                  <button onClick={() => update(active!, { face_up: !a.face_up })} disabled={!a.card_id} className="chip !bg-transparent !border-[#2A2825] !text-[#F6F4EE]" data-testid="flip-btn">Flip</button>
                </div>
                <textarea className="input-ed !bg-[#121110] !border-[#2A2825] !text-[#F6F4EE] mt-3 min-h-[90px]" placeholder="Note for this position…" value={a.note} onChange={(e) => update(active!, { note: e.target.value })} data-testid="slot-note" />
              </>
            ) : <p className="text-sm text-[#A8A59C]">Tap a slot to draw a card. Select a card to add notes, flip or reverse it.</p>}
          </div>
          <div className="rounded-md border border-[#2A2825] bg-[#1A1917] p-4 space-y-2">
            <input className="input-ed !bg-[#121110] !border-[#2A2825] !text-[#F6F4EE]" placeholder="Reading title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="snapshot-title" />
            <textarea className="input-ed !bg-[#121110] !border-[#2A2825] !text-[#F6F4EE] min-h-[110px]" placeholder="Overall interpretation & notes…" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="snapshot-notes" />
          </div>
          {user && (
            <div className="rounded-md border border-[#2A2825] bg-[#1A1917] p-4">
              <p className="eyebrow !text-[#C5A059] mb-2">Saved snapshots · {saved.length}</p>
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {saved.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`saved-snapshot-${s.id}`}>
                    <button onClick={() => loadSnap(s)} className="flex-1 text-left truncate hover:text-[#C5A059]">{s.title}</button>
                    <span className="mono text-[9px] text-[#A8A59C]">{s.slots.filter((x: any) => x.card_id).length}c</span>
                    <button onClick={() => del(s.id)} className="text-[#A8A59C] hover:text-[#F6F4EE]"><Trash2 size={13} /></button>
                  </div>))}
                {saved.length === 0 && <p className="text-xs text-[#A8A59C]">Nothing saved yet.</p>}
              </div>
            </div>)}
        </aside>
      </div>
    </div>
  );
}
